import { useRef } from 'react';
import { Button } from '@citizen-walk/shared-ui';
import type { CardPrintDistrictConfig } from '@citizen-walk/shared-types';
import {
  DEFAULT_CARD_LAYOUT,
  DEFAULT_CARD_THEME,
  type CardThemeLayout,
  type Decoration,
  type DecorationTint,
  type ResolvedCardTheme,
} from './cardTheme';
import type { CardPrintField } from './cardLayout';
import { resolveCardFontId } from './fontRegistry';
import type { CropMarks, FillBehavior } from './jobConfig';
import { applyCardPrintConfig, type AppliedCardPrintConfig } from './cardPrintSettings';
import { sanitizeSvg } from './svgUtils';

/**
 * Sprint BG-DataSettings 14 — Card-print テーマ JSON import/export buttons。
 *
 * 旧 `old/apps/admin/src/features/cardPrint/FormatImportExport.tsx` + `configIO.ts`
 * を L2 で踏襲、v2 では M7 cardPrintTemplates.theme 永続化と整合する subset を扱う。
 *
 * 旧との差分:
 *   - 旧の `CardPrintConfig` (theme + fields + adminNote + startIndex + cropMarks + fillBehavior) のうち、
 *     v2 では fields/layout は M7 master 側で管理されるため `theme` のみを書き出す
 *   - schema id は `citizenwalk.cardPrint.theme.v1` に変更 (旧 `bousaimap.cardPrint.v1` 廃止)
 *   - decoration の `opacity` / `rotation_deg` は v2 shared-types 未定義のため drop
 *
 * v2 拡張 (`citizenwalk.cardPrint.settings.v2`): テーマだけでなく印刷ジョブ設計 (表示項目+書式 /
 * QRサイズ / 行間 / カットライン / 埋め方) も含む「全設定」を書出/読込できる。旧 v1 (テーマのみ) も
 * 後方互換で読み込める (parseImportedSettings)。categoryColors はテーマに含めて round-trip する。
 */

export interface ExportedCardTheme {
  $schema: 'citizenwalk.cardPrint.theme.v1';
  version: 1;
  exportedAt: string;
  theme: ResolvedCardTheme;
}

export interface ImportResult {
  theme: ResolvedCardTheme;
  warnings: string[];
}

/** JSON で round-trip する印刷ジョブ設計 (categoryColors は theme 側に含める)。 */
export interface ExportedJob {
  fields: CardPrintField[];
  qrSize_mm: number;
  fieldGap_mm: number;
  cropMarks: CropMarks;
  fillBehavior: FillBehavior;
}

export interface ExportedCardSettings {
  $schema: 'citizenwalk.cardPrint.settings.v2';
  version: 2;
  exportedAt: string;
  theme: ResolvedCardTheme;
  job: ExportedJob;
}

export interface ImportSettingsResult {
  theme: ResolvedCardTheme;
  /** v2 のジョブ設計 (sanitize 済)。v1 (テーマのみ) 読込時は null。 */
  job: AppliedCardPrintConfig | null;
  warnings: string[];
}

const SCHEMA_NAME = 'citizenwalk.cardPrint.theme.v1';
const SCHEMA_VERSION = 1;
const SETTINGS_SCHEMA_NAME = 'citizenwalk.cardPrint.settings.v2';
const SETTINGS_VERSION = 2;
const HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const VALID_SLOTS = new Set([
  'background-pattern',
  'header-band',
  'footer-band',
  'left-side-band',
  'corner-top-left',
  'corner-top-right',
  'corner-bottom-left',
  'corner-bottom-right',
  'title-icon',
]);
const VALID_TINTS = new Set(['accent', 'primary', 'secondary', 'none']);

function asString(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}
function asNumber(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}
function asHex(v: unknown, fallback: string): string {
  if (typeof v === 'string' && HEX.test(v.trim())) return v.trim();
  return fallback;
}
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function validateDecoration(raw: unknown, warnings: string[]): Decoration | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const slot =
    typeof r.slot === 'string' && VALID_SLOTS.has(r.slot) ? (r.slot as Decoration['slot']) : null;
  if (!slot) {
    warnings.push(`不正な装飾スロット: ${String(r.slot)} を除外`);
    return null;
  }
  const cleaned = sanitizeSvg(asString(r.svgSource));
  if (!cleaned) {
    warnings.push(`スロット ${slot} の SVG をサニタイズできず除外`);
    return null;
  }
  const tintWith =
    typeof r.tintWith === 'string' && VALID_TINTS.has(r.tintWith)
      ? (r.tintWith as DecorationTint)
      : 'none';
  const out: Decoration = {
    slot,
    svgSource: cleaned,
    x_mm: clamp(asNumber(r.x_mm), -91, 91),
    y_mm: clamp(asNumber(r.y_mm), -55, 55),
    width_mm: clamp(asNumber(r.width_mm, 10), 0.1, 91),
    height_mm: clamp(asNumber(r.height_mm, 10), 0.1, 55),
    tintWith,
  };
  if (typeof r.opacity === 'number' && Number.isFinite(r.opacity)) {
    out.opacity = clamp(r.opacity, 0, 1);
  }
  if (typeof r.rotation_deg === 'number' && Number.isFinite(r.rotation_deg)) {
    out.rotation_deg = r.rotation_deg;
  }
  return out;
}

function validateCategoryColors(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'string' && HEX.test(v.trim())) out[k] = v.trim();
  }
  return out;
}

function validateLayout(raw: unknown): CardThemeLayout {
  const base = { ...DEFAULT_CARD_LAYOUT };
  if (!raw || typeof raw !== 'object') return base;
  const r = raw as Record<string, unknown>;
  return {
    photoSide: r.photoSide === 'right' ? 'right' : 'left',
    photoWidth_mm: clamp(asNumber(r.photoWidth_mm, base.photoWidth_mm), 0, 91),
    textPadding_mm: clamp(asNumber(r.textPadding_mm, base.textPadding_mm), 0, 20),
    titleFontSize_pt: clamp(asNumber(r.titleFontSize_pt, base.titleFontSize_pt), 4, 40),
    bodyFontSize_pt: clamp(asNumber(r.bodyFontSize_pt, base.bodyFontSize_pt), 4, 40),
  };
}

/** ResolvedCardTheme → 書き出し payload (v1、テーマのみ)。 */
export function buildExportedTheme(theme: ResolvedCardTheme): ExportedCardTheme {
  return {
    $schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    theme,
  };
}

/** raw theme object (unknown) → ResolvedCardTheme (warnings へ装飾の除外理由を push)。 */
function validateThemeObject(raw: unknown, warnings: string[]): ResolvedCardTheme {
  const themeRaw = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const decRaw = Array.isArray(themeRaw.decorations) ? themeRaw.decorations : [];
  const decorations: Decoration[] = [];
  for (const item of decRaw) {
    const d = validateDecoration(item, warnings);
    if (d) decorations.push(d);
  }
  const textColor = asHex(themeRaw.textColor, DEFAULT_CARD_THEME.textColor);
  const borderRaw = (
    themeRaw.border && typeof themeRaw.border === 'object' ? themeRaw.border : {}
  ) as Record<string, unknown>;
  return {
    backgroundColor: asHex(themeRaw.backgroundColor, DEFAULT_CARD_THEME.backgroundColor),
    accent: asHex(themeRaw.accent, DEFAULT_CARD_THEME.accent),
    secondary: asHex(themeRaw.secondary, DEFAULT_CARD_THEME.secondary),
    textHeading: asHex(themeRaw.textHeading, textColor),
    textColor,
    // fontFamily は font id に正規化 (旧 CSS 文字列も吸収、埋め込み可能フォントのみ)。
    fontFamily: resolveCardFontId(themeRaw.fontFamily),
    logoUrl:
      typeof themeRaw.logoUrl === 'string' && /^https?:\/\//.test(themeRaw.logoUrl)
        ? themeRaw.logoUrl
        : '',
    categoryColors: validateCategoryColors(themeRaw.categoryColors),
    border: {
      color: asHex(borderRaw.color, DEFAULT_CARD_THEME.border.color),
      width_mm: clamp(asNumber(borderRaw.width_mm, DEFAULT_CARD_THEME.border.width_mm), 0, 5),
    },
    layout: validateLayout(themeRaw.layout),
    decorations,
  };
}

/** unknown (JSON.parse 済) → ResolvedCardTheme + warning。schema/version 不一致は throw (v1)。 */
export function parseImportedTheme(parsed: unknown): ImportResult {
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('JSON の形式が不正です');
  }
  const obj = parsed as Record<string, unknown>;
  if (obj.$schema !== SCHEMA_NAME) {
    throw new Error(`スキーマ識別子が一致しません(期待値: ${SCHEMA_NAME})`);
  }
  if (obj.version !== SCHEMA_VERSION) {
    throw new Error(`バージョンが対応していません(対応版: ${SCHEMA_VERSION})`);
  }
  const warnings: string[] = [];
  const theme = validateThemeObject(obj.theme, warnings);
  return { theme, warnings };
}

/** ResolvedCardTheme + ジョブ設計 → 全設定 書き出し payload (v2)。 */
export function buildExportedSettings(
  theme: ResolvedCardTheme,
  job: ExportedJob,
): ExportedCardSettings {
  return {
    $schema: SETTINGS_SCHEMA_NAME,
    version: SETTINGS_VERSION,
    exportedAt: new Date().toISOString(),
    theme,
    job,
  };
}

/**
 * unknown (JSON.parse 済) → 全設定 (theme + job)。
 * - v2 (`citizenwalk.cardPrint.settings.v2`): theme + job を読む。
 * - v1 (`citizenwalk.cardPrint.theme.v1`): テーマのみ (job=null、後方互換)。
 * いずれにも一致しなければ throw。
 */
export function parseImportedSettings(parsed: unknown): ImportSettingsResult {
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('JSON の形式が不正です');
  }
  const obj = parsed as Record<string, unknown>;
  const warnings: string[] = [];
  if (obj.$schema === SETTINGS_SCHEMA_NAME && obj.version === SETTINGS_VERSION) {
    const theme = validateThemeObject(obj.theme, warnings);
    // job 部は CardPrintDistrictConfig 互換 (categoryColors は theme 側)。applyCardPrintConfig で sanitize。
    const job = applyCardPrintConfig(obj.job as CardPrintDistrictConfig | undefined);
    return { theme, job, warnings };
  }
  if (obj.$schema === SCHEMA_NAME && obj.version === SCHEMA_VERSION) {
    const theme = validateThemeObject(obj.theme, warnings);
    return { theme, job: null, warnings };
  }
  throw new Error(`未対応のフォーマットです (schema: ${String(obj.$schema)})`);
}

export interface FormatImportExportProps {
  theme: ResolvedCardTheme;
  /** 現在のジョブ設計 (書き出しに含める)。 */
  job: ExportedJob;
  /** 読込結果 (theme + job) を適用するコールバック。 */
  onImport: (result: ImportSettingsResult) => void;
  disabled?: boolean;
}

export function FormatImportExport({
  theme,
  job,
  onImport,
  disabled,
}: FormatImportExportProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);

  function handleExport(): void {
    const data = buildExportedSettings(theme, job);
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    a.href = url;
    a.download = `card-settings-${date}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleFile(file: File): Promise<void> {
    try {
      const text = await file.text();
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error('JSON の解析に失敗しました');
      }
      const result = parseImportedSettings(parsed);
      onImport(result);
      const scope = result.job ? 'テーマ + 印刷設定' : 'テーマ (旧形式)';
      if (result.warnings.length > 0) {
        alert(`${scope}を読み込みました。\n警告:\n- ${result.warnings.join('\n- ')}`);
      } else {
        alert(`${scope}を読み込みました`);
      }
    } catch (e) {
      alert(`読み込みに失敗しました: ${(e as Error).message}`);
    }
  }

  return (
    <div className="grid grid-cols-1 gap-2" data-testid="card-format-import-export">
      <h3 className="font-bold text-sm text-slate-800">フォーマット書き出し / 読み込み</h3>
      <div className="flex gap-2 flex-wrap">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={handleExport}
          disabled={disabled}
          aria-label="テーマを JSON 書き出し"
        >
          書き出す
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          aria-label="テーマ JSON を読み込み"
        >
          読み込む
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept=".json,application/json"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
            e.target.value = '';
          }}
          aria-label="テーマ JSON ファイル入力"
        />
      </div>
      <p className="text-[10px] text-slate-500">
        テーマ (色・フォント・装飾・カテゴリ色) と 印刷設定 (表示項目・書式・QRサイズ・余白) を
        まとめて JSON で書き出し、別の地区にも同じデザインを適用できます。旧形式 (テーマのみ) の
        ファイルも読み込めます。
      </p>
    </div>
  );
}
